import { Component, Inject, Input, OnDestroy } from "@angular/core";
import {
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepModel,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import {
    ANGULARJS_ROOTSCOPE,
    ANGULARJS_TRANSLATE,
    LOADING_PANE_SERVICE,
    NMS_STATES,
    STATE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Subscription } from "rxjs";
import { EndsStepModel } from "../1-ends/ends-step.model";
import { GeneralStepService } from "./services/general-step.service";
import { DwdmCircuitWizardConverterService } from "../../../services/dwdm-circuit-wizard-converter.service";
import { DwdmCircuitData, DwdmCircuitEndsData, GeneralStepModel } from "./general-step.model";
import { NmsToastrService } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr.service";
import { CircuitDiagram } from "../../../diagram/models/diagram.models";
import { DwdmDiagramWizardConverterService } from "../../diagram/dwdm-diagram-wizard-converter.service";
import { OscManagementStepModel } from "../6-osc-management/osc-management-step.model";
import { TranspondersStepModel } from "../2-transponders/transponders-step.model";
import { IntermediatesStepModel } from "../3-intermediates/intermediates-step.model";
import { LinksStepModel } from "../4-links/links-step.model";
import { AmplifiersStepModel } from "../5-amplifiers/amplifiers-step.model";
import { DwdmCircuitWizardUtilsService } from "../../utils/dwdm-circuit-wizard-utils.service";

/**
 * Step General do DwdmCircuitWizard.
 */
@Component({
    selector: "general-step",
    templateUrl: "./general-step.component.html",
    styleUrls: ["./general-step.component.scss"]
})
export class GeneralStepComponent extends AbstractNmsWizardStep implements OnDestroy {
    /** Hash calculado do circuito original */
    @Input()
    initialCircuitHash: number;

    model: GeneralStepModel;
    endsModel: EndsStepModel;
    transpondersModel: TranspondersStepModel;
    intermediatesModel: IntermediatesStepModel;
    linksModel: LinksStepModel;
    amplifiersModel: AmplifiersStepModel;
    oscsModel: OscManagementStepModel;
    subscriptions: Array<Subscription> = [];
    lastGeneratedName: string;
    lastEndsHash: number;

    circuitDiagram: CircuitDiagram;

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any, @Inject(STATE) private readonly $state: any,
            @Inject(NMS_STATES) private readonly nmsStates: any, @Inject(ANGULARJS_ROOTSCOPE) private readonly $rootScope: any,
            private readonly generalStepService: GeneralStepService, private readonly toastr: NmsToastrService,
            @Inject(LOADING_PANE_SERVICE) private readonly paneService: any,
            private readonly circuitWizardConverterService: DwdmCircuitWizardConverterService,
            private readonly diagramConverterService: DwdmDiagramWizardConverterService,
            private readonly dwdmCircuitWizardUtilsService: DwdmCircuitWizardUtilsService,
            private readonly circuitWizardUtilsService: DwdmCircuitWizardUtilsService) {
        super();
        this.model = new GeneralStepModel();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    goingToPreviousStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    async comingFromNextStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        const model: Promise<NmsWizardStepModel> = this.resolveModel();
        await this.updateDiagramModel(wizardStepsCacheMap);

        return model;
    }

    async comingFromPreviousStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        this.model = wizardStepsCacheMap["GeneralStepModel"] as GeneralStepModel || this.model;
        this.endsModel = wizardStepsCacheMap["EndsStepModel"] as EndsStepModel;
        this.transpondersModel = wizardStepsCacheMap["TranspondersStepModel"] as TranspondersStepModel;
        this.intermediatesModel = wizardStepsCacheMap["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = wizardStepsCacheMap["LinksStepModel"] as LinksStepModel;
        this.amplifiersModel = wizardStepsCacheMap["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = wizardStepsCacheMap["OscManagementStepModel"] as OscManagementStepModel;

        if (this.model.generatedName) {
            this.updateCircuitName();
        }

        await this.updateDiagramModelWithCurrentValues();
        this.stepLoaded = true;

        return this.resolveModel();
    }

    async allowPrevious(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    async allowFinish(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    async finishAction(wizardStepsCacheMap: NmsWizardStepMapData): Promise<void> {
        wizardStepsCacheMap["GeneralStepModel"] = this.model;

        if (this.model.circuitId) {
            await this.showUpdateConfirmation(wizardStepsCacheMap)
        } else {
            await this.createAndActivate(wizardStepsCacheMap);
        }
    }

    async showUpdateConfirmation(wizardStepsCacheMap: NmsWizardStepMapData) {
        const hasChange = this.initialCircuitHash !== this.dwdmCircuitWizardUtilsService.calculateCacheHash(wizardStepsCacheMap);
        const modalMessage = hasChange ? "dwdm.circuit.wizard.updated" : "dwdm.circuit.wizard.no.updated";
        this.loadingConfig.emit({ showLoading: false, showOverlay:false });
        return this.$rootScope
            .showDialog({
                message: this.translate.instant(modalMessage),
                isConfirm: true
            })
            .then(() => {
                if (hasChange){
                    this.loadingConfig.emit({ showLoading: true, showOverlay: true });
                    this.updateAndActivate(wizardStepsCacheMap, this.model.circuitId);
                } else {
                    this.$state.go(this.nmsStates.dwdmCircuits.list);
                }
            }).catch(() => {/* Não faz nada mas precisa declarar o catch para os evento de cancel */});
    }

    changeGenerateName(): void {
        if (!this.model.generatedName) {
            return;
        }

        const currentNameChanged = this.model.name && this.model.name !== this.lastGeneratedName;
        if (currentNameChanged || this.endsChanged()) {
            this.$rootScope
                .showDialog({
                    message: this.translate.instant("dwdm.circuit.wizard.links.name.generate.override.confirm"),
                    isConfirm: true
                })
                .then(() => this.restoreOrGenerateName())
                .catch(() => {
                    this.model.generatedName = false;
                });
            return;
        }

        this.restoreOrGenerateName();
    }

    validate(): Promise<NmsWizardStepValidationResponse> {
        const isValid = !!this.model.name?.trim();
        return Promise.resolve(new NmsWizardStepValidationResponse(isValid));
    }

    resolveModel(): Promise<NmsWizardStepModel> {
        return Promise.resolve(this.model);
    }

    private async updateDiagramModel(cache: NmsWizardStepMapData): Promise<void> {
        this.circuitDiagram = this.diagramConverterService.convert(cache);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    private async updateDiagramModelWithCurrentValues(): Promise<void> {
        await this.resolveModel();
        const stepsModel: NmsWizardStepMapData = {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.transpondersModel,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.model
        };

        this.circuitDiagram = this.diagramConverterService.convert(stepsModel);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    private convertEndsStepToEndsData(endsStep: EndsStepModel): DwdmCircuitEndsData {
        return {
            aEndName: endsStep?.aEndDevice?.name,
            zEndName: endsStep?.zEndDevice?.name,
            aEndPorts: endsStep?.aEndPorts,
            zEndPorts: endsStep?.zEndPorts
        };
    }

    private updateCircuitName(): void {
        const endsData = this.convertEndsStepToEndsData(this.endsModel);
        const generateCircuitNameSubscription = this.generalStepService
            .generateCircuitName(endsData)
            .subscribe((generatedName) => {
                this.model.name = generatedName;
                this.lastGeneratedName = generatedName;
                this.lastEndsHash = this.dwdmCircuitWizardUtilsService.calculateHash(this.endsModel);
            });
        this.subscriptions.push(generateCircuitNameSubscription);
    }

    private async createAndActivate(wizardStepsCacheMap: NmsWizardStepMapData) {
        this.loadingConfig.emit({showLoading: false});
        this.paneService.showLoadingPane("dwdm.circuit.wizard.general.configuring");

        try {
            const result = await this.generalStepService
                .createAndActivate(this.circuitWizardConverterService.convertCacheMapToCircuitData(wizardStepsCacheMap))
                .toPromise();

            if (result == null) {
                this.toastr.success(this.translate.instant("dwdm.circuit.wizard.general.successfuly.added"));
                this.$state.go(this.nmsStates.dwdmCircuits.list);
            }

        } finally {
           this.paneService.closeLoadingPane();
        }
    }

    private async updateAndActivate(wizardStepsCacheMap: NmsWizardStepMapData, circuitId: number) {
        this.loadingConfig.emit({ showLoading: false });
        this.paneService.showLoadingPane("dwdm.circuit.wizard.general.updating");
        const dwdmCircuitData: DwdmCircuitData =
            this.circuitWizardConverterService.convertCacheMapToCircuitData(wizardStepsCacheMap);
        dwdmCircuitData.circuitId = circuitId;

        try {
            const result = await this.generalStepService
                .updateAndActivate(dwdmCircuitData)
                .toPromise();

            if (result == null) {
                this.toastr.success(this.translate.instant("dwdm.circuit.wizard.general.successfuly.updated"));
                this.$state.go(this.nmsStates.dwdmCircuits.list);
            }
        } finally {
            this.paneService.closeLoadingPane();
        }
    }

    private endsChanged(): boolean {
        return this.lastEndsHash !== this.dwdmCircuitWizardUtilsService.calculateHash(this.endsModel);
    }

    private restoreOrGenerateName(): void {
        if (this.lastGeneratedName && !this.endsChanged()) {
            this.model.name = this.lastGeneratedName;
            return;
        }

        this.updateCircuitName();
    }
}
